const DEFAULT_ERROR_MESSAGE = 'Não foi possível concluir esta operação. Tente novamente.';

const TECHNICAL_ERROR_PATTERNS = [
  /request failed with status code\s*\d{3}/i,
  /network error/i,
  /timeout of .* exceeded/i,
  /bad gateway|gateway timeout|service unavailable|internal server error/i,
  /unexpected error occurred|an unexpected error/i,
  /stack trace|exception|sql(?:state)?|jdbc|hibernate|axios|upstream/i,
  /failed to [^.!?\n]*:/i,
];

/**
 * Returns whether a message looks like an implementation/network error.
 * These messages are useful in server logs, but must never be rendered in
 * the customer-facing UI.
 */
export const isTechnicalErrorMessage = (value) => {
  if (typeof value !== 'string') return false;
  const message = value.trim();
  if (!message) return false;
  return TECHNICAL_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

const getResponseMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === 'string') return data;
  if (data && typeof data.message === 'string') return data.message;
  return null;
};

/**
 * Converts an Axios/API error to safe, Portuguese UI copy.
 *
 * Never fall back to error.message: Axios uses that field for implementation
 * details such as "Request failed with status code 502". Safe business
 * messages from 4xx responses are preserved; technical/5xx messages are not.
 */
export const getUserFacingErrorMessage = (error, fallback = DEFAULT_ERROR_MESSAGE) => {
  const safeFallback = typeof fallback === 'string' && fallback.trim()
    ? fallback.trim()
    : DEFAULT_ERROR_MESSAGE;
  const status = error?.response?.status;
  const responseMessage = getResponseMessage(error);

  if (status === 401) return 'A sessão expirou. Inicia sessão novamente.';
  if (status === 403) return 'Não tens autorização para realizar esta ação.';
  if (status === 404) return 'Não foi possível encontrar o recurso solicitado.';
  if (status === 409) return responseMessage && !isTechnicalErrorMessage(responseMessage)
    ? responseMessage
    : 'Não foi possível concluir porque os dados já foram alterados.';
  if (status === 413) return 'O ficheiro excede o limite permitido. Vídeos: 200 MB; imagens: 20 MB.';
  if (status === 429) return 'Há demasiados pedidos neste momento. Tenta novamente daqui a pouco.';

  // A 5xx response, or a response that contains a technical message, must
  // never leak backend/Axios details to the browser UI.
  if ((typeof status === 'number' && status >= 500) || isTechnicalErrorMessage(responseMessage)) {
    return safeFallback;
  }

  if (responseMessage && !isTechnicalErrorMessage(responseMessage)) {
    return responseMessage.trim();
  }

  return safeFallback;
};

export default getUserFacingErrorMessage;
